/**
 * Copyright Mántum All Rights Reserved.
 */
import Label from '../label';
import Text from '../../common/button/text';
import getMessage from '../../../locales';
import store from '../internal/store';
import useSWR from 'swr';
import { ACTION_VALUE, useGroup } from '../../group';
import { nanoid } from 'nanoid';
import { validator } from '@mantum/kanela';

import {
    Suspense,
    forwardRef,
    lazy,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    KEY_FIELD,
    NAME_FIELD,
    SIZE_FIELD,
    TYPE_FIELD,
    URL_FIELD
} from './constant';

import type {
    DataGrid as Component,
    DataSource,
    Locale,
    Validation
} from '@mantum/kanela';

export type PropTypes = {
    advanced?: {
        accept?: string;
        entity?: string;
        filter?: string[];
        host?: string;
        multiple?: boolean;
        namespace?: string;
        refresh?: number;
        revalidate?: boolean;
        security?: {
            key: string;
            secret: string;
        };
    };
    dataSource?: DataSource;
    error?: boolean;
    label?: string;
    locale?: Locale;
    name?: string;
    onChange?: (name?: string, value?: unknown) => void;
    optional?: boolean;
    placeholder?: string;
    readonly?: boolean;
    testid?: string;
    validation?: Validation;
};

const DataGrid = lazy(() => {
    return import('@mantum/kanela-react-datagrid-adapter');
});

export default forwardRef<Component, PropTypes>(function Component(props, ref) {
    // prettier-ignore
    const { 
        advanced = {},
        readonly = false,
        locale = 'en',
        dataSource = {
            url: `${props.advanced?.host}/api/storage/${props.advanced?.namespace}/${props.advanced?.entity}`,
            method: "GET",
            namespace: "body",
            headers: {
                MantumKey: props.advanced?.security?.key,
                MantumSecret: props.advanced?.security?.secret
            }
        } as DataSource,
        name, 
        onChange,
    } = props;

    // prettier-ignore
    const {
        filter = [],
        multiple = true,
        refresh = 0,
        revalidate = true,
    } = advanced

    const config = {
        refreshInterval: refresh,
        revalidateOnFocus: revalidate
    };

    const groups = useGroup();

    const filesRef = useRef<File[]>([]);
    const categoryRef = useRef<unknown[]>([]);

    const changeRef = useRef(onChange);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [files, setFiles] = useState<unknown[]>([]);

    const fetcher = () => store(dataSource);
    const resolve = () => {
        if (props.advanced?.host && validator.isDataSource(dataSource)) {
            if (!validator.isUndefined(dataSource.url)) {
                return dataSource.url;
            }
        }
        return null;
    };

    const { data = [] } = useSWR(resolve, fetcher, config);

    useEffect(() => {
        changeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (!name || !groups) {
            return;
        }

        if (!(name in groups)) {
            return;
        }

        const content = groups[name];
        if (content.action === ACTION_VALUE) {
            if (!content.value || content.value === '') {
                setFiles([]);
                if (ref && 'current' in ref) {
                    ref?.current?.clear();
                }

                if (changeRef.current) {
                    changeRef.current(name, {
                        files: [],
                        categories: []
                    });
                }
            }
        }
    }, [name, groups, ref]);

    const getFile = (key: string) => {
        return filesRef.current.find((it) => {
            if (KEY_FIELD in it) {
                return it.key === key;
            }
            return false;
        });
    };

    const onClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const onInputFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const files = Array.from(event.target.files).map((it) => {
                Object.assign(it, {
                    key: nanoid()
                });
                return it;
            });

            filesRef.current = [...files, ...filesRef.current];
            setFiles((previous) => [
                ...previous,
                ...files.map((it) => ({
                    key: KEY_FIELD in it ? `${it?.[KEY_FIELD]}` : '',
                    name: it?.[NAME_FIELD],
                    size: `${(it?.[SIZE_FIELD] / 1000).toFixed()} kb`,
                    type: it?.[TYPE_FIELD],
                    url: URL_FIELD in it ? `${it?.[URL_FIELD]}` : '',
                    external: false
                }))
            ]);

            if (changeRef.current) {
                changeRef.current(name, {
                    files: filesRef.current,
                    categories: categoryRef.current
                });
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }

        event.preventDefault();
        event.stopPropagation();
    };

    const onDataGridChange = (_?: string, values?: unknown) => {
        if (!Array.isArray(values)) {
            return;
        }

        if (values.length === 0) {
            filesRef.current = [];
            if (changeRef.current) {
                changeRef.current(name, {
                    files: [],
                    categories: []
                });
            }
            return;
        }

        filesRef.current = filesRef.current.filter((it) => {
            if (KEY_FIELD in it) {
                return (
                    values.find((value) => {
                        return value.key === it?.[KEY_FIELD];
                    }) !== undefined
                );
            }
            return false;
        });

        categoryRef.current = values
            .map((it, index) => ({
                file: index,
                category: it.category
            }))
            .filter((it) => it.category);

        if (changeRef.current) {
            changeRef.current(name, {
                files: filesRef.current,
                categories: categoryRef.current
            });
        }
    };

    const onRowRemoved = (values?: Record<string, unknown>) => {
        if (!values || !(KEY_FIELD in values)) {
            return;
        }

        setFiles((previous) => {
            return previous.filter((it) => {
                return (
                    typeof it === 'object' &&
                    it &&
                    KEY_FIELD in it &&
                    it.key !== values.key
                );
            });
        });
    };

    const onViewFile = (rows: unknown) => {
        if (!Array.isArray(rows)) {
            return;
        }

        const [row] = rows;
        if (!row) {
            return;
        }

        let url = '';
        if (row.external) {
            url = row.url;
        } else {
            const file = getFile(row.key);
            if (file) {
                url = URL.createObjectURL(file);
            }
        }

        if (url) {
            window.open(url, '_blank');
            if (ref && 'current' in ref) {
                ref.current?.clearSelection();
            }
        }
    };

    const allowUpdating = useMemo(() => {
        return props.advanced?.host ? true : false;
    }, [props.advanced?.host]);

    // prettier-ignore
    const source = [
        ...files, 
        ...data
    ];

    const visible = readonly || source.length > 0 ? '' : 'hidden';

    return (
        <div className='w-full flex-row'>
            <div className='flex w-full items-start justify-between'>
                <Label>{props.label}</Label>
                {!readonly && (
                    <div className='w-40'>
                        <Text onClick={onClick} testid={props.testid}>
                            <div className='flex items-center justify-center'>
                                <span>{props.placeholder}</span>
                                <input
                                    accept={props.advanced?.accept}
                                    className='pointer-events-none hidden opacity-0'
                                    data-testid='file-input'
                                    multiple={multiple}
                                    onChange={onInputFileChange}
                                    ref={fileInputRef}
                                    type='file'
                                />
                            </div>
                        </Text>
                    </div>
                )}
            </div>
            <div className={`mt-4 ${visible}`}>
                <Suspense fallback={<div>{getMessage('Loading', locale)}</div>}>
                    <DataGrid
                        advanced={{
                            allowDeleting: !readonly,
                            allowRefresh: readonly,
                            allowUpdating,
                            entity: 'key',
                            height: 'auto'
                        }}
                        readonly={readonly}
                        columns={[
                            {
                                name: 'name',
                                label: getMessage('File', locale),
                                type: 'string',
                                allowHiding: false,
                                optional: true,
                                readonly: true,
                                order: 1
                            },
                            {
                                name: 'size',
                                label: getMessage('Size', locale),
                                type: 'string',
                                visible: false,
                                optional: true,
                                readonly: true,
                                order: 2
                            },
                            {
                                name: 'type',
                                label: getMessage('Type', locale),
                                type: 'string',
                                optional: true,
                                readonly: true,
                                order: 3
                            },
                            props.advanced?.host && {
                                name: 'category',
                                label: getMessage('Category', locale),
                                type: 'select',
                                dataSource: {
                                    url: `${props.advanced?.host}/api/category/search/client`,
                                    method: 'GET',
                                    namespace: 'body',
                                    parameter: {
                                        filter: filter.join(','),
                                        active: true
                                    },
                                    headers: {
                                        MantumKey:
                                            props.advanced?.security?.key,
                                        MantumSecret:
                                            props.advanced?.security?.secret
                                    }
                                },
                                options: {
                                    label: 'name',
                                    value: 'id'
                                },
                                allowHiding: false,
                                optional: true,
                                order: 4
                            }
                        ].filter(Boolean)}
                        dataSource={source}
                        name={name}
                        onRowRemoved={onRowRemoved}
                        onChange={onDataGridChange}
                        onClick={onViewFile}
                        ref={ref}
                        summaries={[
                            {
                                column: 'name',
                                type: 'count',
                                name: 'total',
                                format: getMessage('Attached files', locale)
                            }
                        ]}
                    />
                </Suspense>
            </div>
        </div>
    );
});
